"use client";

import { useEffect, useRef, useState } from "react";
import type {
  IUseEventDispatchEvent,
  TViewEvent,
} from "@tracking-events/browser";

import useIntersectionObserver from "../useIntersectionObserver";
import { useEvent } from "./useEvent";

export type TUseEngagementTrackerOptions = {
  accumulate?: boolean;
  duration?: number;
  threshold?: number;
  event: IUseEventDispatchEvent<TViewEvent>;
};

export const useEngagementTracker = <
  T extends HTMLElement = HTMLDivElement,
>({
  accumulate = false,
  duration = 3000,
  threshold = 0.5,
  event,
}: TUseEngagementTrackerOptions) => {
  const { dispatchEvent } = useEvent();
  const [ ref, isVisible ] = useIntersectionObserver<T>({
    root: null,
    rootMargin: "0px",
    threshold,
  });
  const [ hasEngaged, setHasEngaged ] = useState(false);

  const elapsedRef = useRef(0);
  const visibleSinceRef = useRef<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hasEngagedRef = useRef(false);
  const eventRef = useRef(event);
  const dispatchEventRef = useRef(dispatchEvent);

  useEffect(() => {
    eventRef.current = event;
    dispatchEventRef.current = dispatchEvent;
  }, [ dispatchEvent, event ]);

  useEffect(() => {
    if (hasEngaged) { return; }

    const pause = () => {
      if (visibleSinceRef.current !== null) {
        elapsedRef.current += performance.now() - visibleSinceRef.current;
        visibleSinceRef.current = null;
      }

      if (timeoutRef.current !== undefined) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
    };

    const engage = () => {
      if (hasEngagedRef.current) { return; }

      pause();
      hasEngagedRef.current = true;
      setHasEngaged(true);

      const { name, properties } = eventRef.current;
      dispatchEventRef.current(name, properties);
    };

    const resume = () => {
      if (
        visibleSinceRef.current !== null ||
        document.hidden ||
        !isVisible
      ) {
        return;
      }

      const remaining = duration - elapsedRef.current;

      if (remaining <= 0) {
        engage();
        return;
      }

      visibleSinceRef.current = performance.now();
      timeoutRef.current = setTimeout(engage, remaining);
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        pause();
        if (!accumulate) { elapsedRef.current = 0; }
      } else {
        resume();
      }
    };

    if (!isVisible && !accumulate) { elapsedRef.current = 0; }

    resume();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      pause();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [ accumulate, duration, hasEngaged, isVisible ]);

  return {
    ref,
    isVisible,
    hasEngaged,
  };
};
